import { useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "./Checkbox";
import { useClickOutside } from "./useClickOutside";

// Deterministic "today" for the prototype (matches the dashboard captions).
const TODAY = new Date(2026, 6, 15);
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function sameDay(a: Date | null, b: Date | null) {
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function fmtShort(d: Date) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function bucketForDays(days: number): string {
  if (days <= 7) return "This week";
  if (days <= 31) return "This month";
  if (days <= 120) return "This semester";
  return "This year";
}

type Preset = { label: string; start: Date; end: Date };
const PRESETS: Preset[] = [
  { label: "Today", start: startOfDay(TODAY), end: startOfDay(TODAY) },
  { label: "Yesterday", start: addDays(TODAY, -1), end: addDays(TODAY, -1) },
  { label: "Last 7 days", start: addDays(TODAY, -6), end: startOfDay(TODAY) },
  { label: "Last 15 days", start: addDays(TODAY, -14), end: startOfDay(TODAY) },
  { label: "Last 30 days", start: addDays(TODAY, -29), end: startOfDay(TODAY) },
  {
    label: "Last month",
    start: new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1),
    end: new Date(TODAY.getFullYear(), TODAY.getMonth(), 0),
  },
];

export function PeriodPicker({
  label,
  onApply,
  onRemove,
}: {
  label: string;
  onApply: (bucket: string, label: string) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(
    new Date(TODAY.getFullYear(), TODAY.getMonth(), 1),
  );
  const [start, setStart] = useState<Date | null>(addDays(TODAY, -6));
  const [end, setEnd] = useState<Date | null>(startOfDay(TODAY));
  const [preset, setPreset] = useState<string | null>("Last 7 days");
  const [includeNotStarted, setIncludeNotStarted] = useState(true);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));

  const pickPreset = (p: Preset) => {
    setStart(p.start);
    setEnd(p.end);
    setPreset(p.label);
    setView(new Date(p.end.getFullYear(), p.end.getMonth(), 1));
  };

  const pickDay = (d: Date) => {
    setPreset(null);
    if (!start || (start && end)) {
      setStart(d);
      setEnd(null);
    } else if (start && !end) {
      if (d < start) setStart(d);
      else setEnd(d);
    }
  };

  const apply = () => {
    if (start && end) {
      const days =
        Math.round(
          (startOfDay(end).getTime() - startOfDay(start).getTime()) / 86400000,
        ) + 1;
      const lbl = preset ?? `${fmtShort(start)} – ${fmtShort(end)}`;
      onApply(bucketForDays(days), lbl);
    }
    setOpen(false);
  };

  const remove = () => {
    onRemove();
    setOpen(false);
  };

  const monthStart = new Date(view.getFullYear(), view.getMonth(), 1);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const cells: Date[] = Array.from({ length: 42 }, (_, i) =>
    addDays(gridStart, i),
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-[6px] border border-[#28543D] bg-[#28543D] px-4 py-[9px] text-sm font-semibold text-white"
      >
        <span className="font-semibold text-white/75">Period:</span>
        <span>{label}</span>
        <ChevronDown
          size={15}
          strokeWidth={2.4}
          className={`text-white transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-[60] grid w-[560px] grid-cols-[150px_1fr] gap-5 rounded-[11px] border border-[#DED8CC] bg-[#FBF8F1] p-5 shadow-[0_16px_48px_rgba(18,18,18,0.18)]">
          {/* presets + actions */}
          <div className="flex flex-col gap-3.5">
            <div className="flex flex-col gap-3">
              {PRESETS.map((p) => {
                const on = preset === p.label;
                return (
                  <button
                    key={p.label}
                    onClick={() => pickPreset(p)}
                    className={`cursor-pointer border-none bg-transparent p-0 text-left text-[15px] ${
                      on
                        ? "font-bold text-[#28543D]"
                        : "font-medium text-[#294B39]"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={apply}
              className="mt-1 w-fit cursor-pointer rounded-[6px] border-none bg-[#28543D] hover:bg-[#204833] px-[22px] py-2.5 text-[15px] font-bold text-white"
            >
              Apply
            </button>
            <button
              onClick={remove}
              className="w-fit cursor-pointer border-none bg-transparent p-0 text-sm font-bold text-[#28543D]"
            >
              Remove filter
            </button>
            <div
              className="flex cursor-pointer items-center gap-2.5"
              onClick={() => setIncludeNotStarted((v) => !v)}
            >
              <Checkbox checked={includeNotStarted} />
              <span className="text-sm text-[#294B39]">
                Include Not Started
              </span>
            </div>
          </div>

          {/* calendar */}
          <div className="rounded-[11px] border border-[#DED8CC] p-4">
            <div className="mb-2 flex items-center justify-between gap-3 border-b border-[#DED8CC] pb-3">
              <span className="text-[19px] font-bold text-[#294B39]">
                {view.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <div className="flex gap-2">
                {[-1, 1].map((dir) => (
                  <button
                    key={dir}
                    onClick={() =>
                      setView(
                        new Date(view.getFullYear(), view.getMonth() + dir, 1),
                      )
                    }
                    className="flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-full border-none bg-[#28543D] text-white"
                  >
                    {dir === -1 ? (
                      <ChevronLeft size={18} strokeWidth={2.4} />
                    ) : (
                      <ChevronRight size={18} strokeWidth={2.4} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-7">
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  className="py-1.5 text-center text-[13px] font-bold text-[#294B39]"
                >
                  {w}
                </div>
              ))}
              {cells.map((d, i) => {
                const outside = d.getMonth() !== view.getMonth();
                const isStart = sameDay(d, start);
                const isEnd = sameDay(d, end);
                const endpoint = isStart || isEnd;
                const inRange =
                  !!start &&
                  !!end &&
                  startOfDay(d) >= startOfDay(start) &&
                  startOfDay(d) <= startOfDay(end);
                const bandRadius =
                  isStart && isEnd
                    ? "9999px"
                    : isStart
                      ? "9999px 0 0 9999px"
                      : isEnd
                        ? "0 9999px 9999px 0"
                        : "0";
                return (
                  <button
                    key={i}
                    onClick={() => pickDay(d)}
                    className="flex h-[42px] cursor-pointer items-center justify-center border-none p-0"
                    style={{
                      background:
                        inRange && !endpoint ? "#EEF0E3" : "transparent",
                      borderRadius: inRange ? bandRadius : "0",
                    }}
                  >
                    <span
                      className="flex h-[34px] w-[34px] items-center justify-center rounded-full text-[15px] font-bold"
                      style={{
                        background: endpoint
                          ? "#070717"
                          : inRange
                            ? "#E2E2E2"
                            : "transparent",
                        color: endpoint
                          ? "#FFFFFF"
                          : outside
                            ? "rgba(18,18,18,0.28)"
                            : "#070717",
                      }}
                    >
                      {d.getDate()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
